import { useId } from 'react';
import {
  ART_HOLE,
  colorForName,
  FALLBACK_FRAME,
  FRAME_VISUALS,
  frameArtUrl,
  initialOf,
} from './crests.js';

/*
 * Аватар гильдии: картинка или буква названия в обводке.
 *
 * Всё рисуется одним SVG в системе координат 0..100.
 *
 * Обводка двух видов (crests.ts):
 *   vector — металлическое кольцо из <circle stroke>. Векторный круг браузер
 *            сглаживает на любой плотности экрана.
 *   art    — рисованная картинка <image> поверх содержимого. Она шире
 *            аватара в 1 / ART_HOLE раз и выходит за его края (overflow:
 *            visible у SVG); место под выступ резервирует родитель.
 *
 * Слои vector снаружи внутрь:
 *   1. тень под кольцом — отделяет его от фона
 *   2. металлическое кольцо — линейный градиент «свет сверху»
 *   3. тонкий блик по верхней дуге — кольцо читается выпуклым
 *   4. перелив: бегущий блик (glint) или радужное кольцо (prism)
 *   5. тёмный зазор — картинка не прилипает к металлу
 *   6. содержимое: картинка или буква на градиентном кружке
 *
 * Перелив крутится через transform: rotate группы. Градиент внутри не
 * анимируется — иначе слой перерисовывался бы каждый кадр (решение 9).
 */
interface GuildAvatarProps {
  readonly frameId: string;
  /** Имя файла рисованной обводки (frames.csv, колонка art). Пусто — металлическое кольцо. */
  readonly frameArt?: string | undefined;
  /** Буква и цвет кружка, когда логотипа нет. */
  readonly name: string;
  readonly logoUrl?: string | undefined;
  /** Размер в CSS-пикселях. */
  readonly size?: number;
}

export function GuildAvatar({
  frameId,
  frameArt,
  name,
  logoUrl,
  size = 64,
}: GuildAvatarProps): React.ReactElement {
  // Уникальные id градиентов: на экране несколько аватаров одновременно.
  const uid = useId().replace(/:/g, '');
  const art = frameArt !== undefined && frameArt !== '' ? frameArt : null;

  return (
    <svg
      className={art === null ? 'avatar' : 'avatar avatar--art'}
      width={size}
      height={size}
      viewBox="0 0 100 100"
      role="img"
      aria-label={name}
    >
      {art === null ? (
        <VectorFrame uid={uid} frameId={frameId} name={name} logoUrl={logoUrl} size={size} />
      ) : (
        <ArtFrame uid={uid} art={art} name={name} logoUrl={logoUrl} size={size} />
      )}
    </svg>
  );
}

/** Содержимое аватара: загруженная картинка или буква на цветном кружке. */
function Content({
  uid,
  name,
  logoUrl,
  r,
}: {
  readonly uid: string;
  readonly name: string;
  readonly logoUrl: string | undefined;
  readonly r: number;
}) {
  const fillId = `f${uid}`;
  const clipId = `c${uid}`;
  const base = colorForName(name);
  const letterSize = r * 1.05;

  return (
    <>
      <defs>
        <linearGradient id={fillId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor={base} stopOpacity="0.85" />
          <stop offset="1" stopColor={base} />
        </linearGradient>
        <clipPath id={clipId}>
          <circle cx="50" cy="50" r={r} />
        </clipPath>
      </defs>
      {logoUrl === undefined ? (
        <>
          <circle cx="50" cy="50" r={r} fill={`url(#${fillId})`} />
          {/*
           * dominant-baseline="central" ставит в центр середину кегля, а не
           * прописной буквы — буква садилась ниже центра. Базовая линия
           * считается от высоты прописной Inter (0.727 кегля).
           */}
          <text
            className="avatar__letter"
            x="50"
            y={50 + letterSize * 0.3635}
            textAnchor="middle"
            fontSize={letterSize}
          >
            {initialOf(name)}
          </text>
        </>
      ) : (
        <image
          href={logoUrl}
          x={50 - r}
          y={50 - r}
          width={r * 2}
          height={r * 2}
          preserveAspectRatio="xMidYMid slice"
          clipPath={`url(#${clipId})`}
        />
      )}
    </>
  );
}

/*
 * Рисованная обводка. Содержимое занимает круг почти во весь аватар,
 * картинка обводки — квадрат шириной 100 / ART_HOLE с центром в 50,50.
 * Отверстие в файле на 1–2 % уже аватара: край содержимого прячется под
 * рамкой без тёмной щели.
 */
function ArtFrame({
  uid,
  art,
  name,
  logoUrl,
  size,
}: {
  readonly uid: string;
  readonly art: string;
  readonly name: string;
  readonly logoUrl: string | undefined;
  readonly size: number;
}) {
  const span = 100 / ART_HOLE;
  const offset = 50 - span / 2;
  const dpr = typeof window === 'undefined' ? 2 : window.devicePixelRatio || 1;

  return (
    <>
      <Content uid={uid} name={name} logoUrl={logoUrl} r={49} />
      <image
        className="avatar__art"
        href={frameArtUrl(art, size, dpr)}
        x={offset}
        y={offset}
        width={span}
        height={span}
      />
    </>
  );
}

function VectorFrame({
  uid,
  frameId,
  name,
  logoUrl,
  size,
}: {
  readonly uid: string;
  readonly frameId: string;
  readonly name: string;
  readonly logoUrl: string | undefined;
  readonly size: number;
}) {
  const frame = FRAME_VISUALS[frameId] ?? FALLBACK_FRAME;
  const [light, mid, dark] = frame.metal;

  const metalId = `m${uid}`;
  const glintId = `g${uid}`;
  const prismId = `p${uid}`;

  // Геометрия в системе 0..100, SVG масштабирует её без потерь.
  // На мелких аватарах кольцо в долях было тоньше двух пикселей и пропадало.
  const minWidth = size <= 56 ? 6 : 3;
  const ringWidth = Math.max(minWidth, frame.weight * 100);
  const ringR = 50 - ringWidth / 2 - 1;
  const gap = 1.4;
  const innerR = ringR - ringWidth / 2 - gap;

  return (
    <>
      <defs>
        {/*
         * Металл: свет сверху, тень в середине, отражённый свет снизу.
         * Один переход «светло → темно» читался как серый пластик.
         */}
        <linearGradient id={metalId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor={light} />
          <stop offset="0.38" stopColor={mid} />
          <stop offset="0.72" stopColor={dark} />
          <stop offset="1" stopColor={mid} />
        </linearGradient>
        {/* Блик: яркое пятно с прозрачными краями, вдоль которого бежит свет. */}
        <linearGradient id={glintId} x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" stopColor="#ffffff" stopOpacity="0" />
          <stop offset="0.5" stopColor="#ffffff" stopOpacity="0.95" />
          <stop offset="1" stopColor="#ffffff" stopOpacity="0" />
        </linearGradient>
        {frame.prism !== undefined && (
          <linearGradient id={prismId} x1="0" y1="0" x2="1" y2="1">
            {frame.prism.map(([offset, color]) => (
              <stop key={offset} offset={offset} stopColor={color} />
            ))}
          </linearGradient>
        )}
      </defs>

      <Content uid={uid} name={name} logoUrl={logoUrl} r={innerR} />

      {/* Зазор: тонкая тёмная линия между содержимым и металлом */}
      <circle cx="50" cy="50" r={innerR + gap / 2} fill="none" stroke="#0d0d10" strokeWidth={gap} />

      {/* Кольцо */}
      <g className="avatar__ring">
        {/* Тёмный кант снаружи: кольцо не тонет в фоне нижней дугой. */}
        <circle
          cx="50"
          cy="50"
          r={ringR}
          fill="none"
          stroke="#000000"
          strokeOpacity="0.6"
          strokeWidth={ringWidth + 1.2}
        />
        <circle
          cx="50"
          cy="50"
          r={ringR}
          fill="none"
          stroke={`url(#${metalId})`}
          strokeWidth={ringWidth}
        />
        {/* Радужный перелив поверх металла: вращается целиком. */}
        {frame.shimmer === 'prism' && (
          <g className="avatar__spin avatar__spin--slow">
            <circle
              cx="50"
              cy="50"
              r={ringR}
              fill="none"
              stroke={`url(#${prismId})`}
              strokeOpacity="0.85"
              strokeWidth={ringWidth}
            />
          </g>
        )}
        {/* Блик по верхней дуге: кольцо читается выпуклым, а не плоским */}
        <path
          d={arc(50, 50, ringR, 215, 325)}
          fill="none"
          stroke="#ffffff"
          strokeOpacity={size <= 56 ? 0.75 : 0.55}
          strokeWidth={size <= 56 ? ringWidth * 0.45 : Math.max(0.8, ringWidth * 0.28)}
          strokeLinecap="round"
        />
        {/* Бегущий блик: короткая дуга обегает кольцо, металл переливается. */}
        {frame.shimmer !== 'none' && (
          <g className="avatar__spin">
            <path
              d={arc(50, 50, ringR, 300, 345)}
              fill="none"
              stroke={`url(#${glintId})`}
              strokeWidth={ringWidth * 0.9}
              strokeLinecap="round"
            />
          </g>
        )}
      </g>
    </>
  );
}

/** Дуга окружности от угла a до угла b (градусы, 0 — справа, по часовой). */
function arc(cx: number, cy: number, r: number, a: number, b: number): string {
  const rad = (d: number) => (d * Math.PI) / 180;
  const x1 = cx + r * Math.cos(rad(a));
  const y1 = cy + r * Math.sin(rad(a));
  const x2 = cx + r * Math.cos(rad(b));
  const y2 = cy + r * Math.sin(rad(b));
  const large = b - a > 180 ? 1 : 0;
  return `M ${x1.toFixed(2)} ${y1.toFixed(2)} A ${r} ${r} 0 ${large} 1 ${x2.toFixed(2)} ${y2.toFixed(2)}`;
}
